import { UplotTimeSeries, UplotTimeSeriesChart } from "@/components/charts/chart.types";
import { formatDurationValue, isTimeUnit } from "@/utils/duration-units";
import { prettyFormatBytes, shortFormatNumber } from "@/utils/string-utils";

/**
 * Hacky but we want to be flexible here
 */
export function seriesHasTimeUnits(series: UplotTimeSeries): boolean {
  return series.unit === "seconds";
}

const DISPLAY_MILLIS_THRESHOLD = 1.2;

export function seriesHasMillis(series: UplotTimeSeries): boolean {
  return (
    seriesHasTimeUnits(series) &&
    series.points.every((p) => p == null || p < DISPLAY_MILLIS_THRESHOLD)
  );
}

export const CHART_MILLISECONDS_LABEL = "milliseconds";
export const CHART_BYTES_LABEL = "bytes";
export const CHART_BYTES_PER_SECOND_LABEL = "bytes/second";

export const allSeriesHaveSameUnits = (series: UplotTimeSeries[]): boolean =>
  new Set(series.map((s) => s.unit)).size === 1;

export const allSeriesHaveMilliseconds = (series: UplotTimeSeries[]): boolean =>
  series.every((s) => seriesHasMillis(s));

/**
 * We display "formatted" units like `GB/s` inline as axis tick labels, because the unit label
 * depends on the actual number. These "formatted" units shouldn't be displayed vertically in the chart axis.
 */
export const unitsAreDisplayedInline = (units: string | undefined): boolean => {
  // Time units and bytes fold their unit into each tick/value ("69.28 s", "12Gi"), so there's no
  // separate axis-title unit to show.
  return units === CHART_BYTES_LABEL || units === CHART_BYTES_PER_SECOND_LABEL || isTimeUnit(units);
};

/**
 * Fixme: this is memoized only on the chart and not on `overrideUnits`, but this is probably
 * acceptable for our purposes, as it is unlikely we will be calling this with the same
 * chart and a different override
 */
export const chartUnitsFromChart = (
  chart: UplotTimeSeriesChart,
  overrideUnits: string | undefined | null,
  hiddenSeries?: Set<string>,
): string | undefined => {
  // specifically if it set to the value null, return undefined.
  // This indicates that we are turning off units
  if (overrideUnits !== undefined) {
    return overrideUnits ?? undefined;
  }

  const series = chart.series.filter((s) => !hiddenSeries?.has(s.label));

  if (series.length === 0) {
    return undefined;
  }

  if (series.length === 1) {
    const onlySeries = series[0];
    const renderMillis = seriesHasMillis(onlySeries);
    return renderMillis ? CHART_MILLISECONDS_LABEL : onlySeries.unit;
  }

  if (allSeriesHaveSameUnits(series)) {
    return allSeriesHaveMilliseconds(series) ? CHART_MILLISECONDS_LABEL : series[0].unit;
  } else {
    return "multiple units";
  }
};

export type ValueFormatter = (value: number) => string;

const valueFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 3,
});

/**
 * Use rounded byte formatter for chart axes.
 */
export const getFormatterFromLabelForChartAxes = (
  label: string | undefined,
  defaultFormatter: ValueFormatter = valueFormatter.format,
): ValueFormatter => {
  let formatter: (num: number) => string = defaultFormatter;

  if (label === CHART_MILLISECONDS_LABEL) {
    formatter = (point) => defaultFormatter(point * 1000);
  } else if (label === CHART_BYTES_LABEL) {
    formatter = prettyFormatBytes;
  } else if (label === CHART_BYTES_PER_SECOND_LABEL) {
    formatter = (point) => `${prettyFormatBytes(point)}/s`;
  } else if (isTimeUnit(label)) {
    // Scale a duration axis to a human unit ("69.28 s") instead of raw ns ("billions").
    formatter = (point) => formatDurationValue(point, label as string);
  }

  return formatter;
};

/**
 * Intentionally a separate parallel implementation from the chart axis label formatter.
 * We want to show rounded numbers like "12Gi", "8Mi", etc. for the chart axes, but for the actual
 * tooltip we want to show more precision so that users aren't confused by seeing a metric series that
 * clearly changes its y-axis position but shows the same rounded "12Gi" value in the tooltip.
 */
export const getFormatterFromLabel = (
  label: string | undefined,
  defaultFormatter: ValueFormatter = valueFormatter.format,
): ValueFormatter => {
  let formatter: (num: number) => string = defaultFormatter;

  if (label === CHART_MILLISECONDS_LABEL) {
    formatter = (point) => defaultFormatter(point * 1000);
  } else if (label === CHART_BYTES_LABEL) {
    formatter = (x) => prettyFormatBytes(x, { binary: false, decimals: 3 });
  } else if (label === CHART_BYTES_PER_SECOND_LABEL) {
    formatter = (point) => `${prettyFormatBytes(point, { binary: false, decimals: 3 })}/s`;
  } else if (isTimeUnit(label)) {
    formatter = (point) => formatDurationValue(point, label as string);
  }

  return formatter;
};

export const formatValueWithLabel = (
  value: number,
  label: string | undefined,
  defaultFormatter: ValueFormatter = valueFormatter.format,
): string => {
  const formatter = getFormatterFromLabel(label, defaultFormatter);

  return formatter(value);
};

export const formatSeriesWithLabel = (
  series: number[],
  label: string | undefined,
  defaultFormatter: ValueFormatter = shortFormatNumber,
): string[] => {
  const formatter = getFormatterFromLabelForChartAxes(label, defaultFormatter);

  return series.map((v) => formatter(v));
};
